import {
  Box,
  Columns,
  Heading,
  Spinner,
  Stack,
  Text,
} from '@spaceship-fspl/components';
import { SaverPortfolio } from '@spaceship-fspl/graphql';
import React from 'react';

interface CompanyProfileProps {
  isLoading?: boolean;
  portfolio?: SaverPortfolio;
  profile?: {
    id: string;
    bodyText: string;
  } | null;
}

export interface ProfileSection {
  header: string;
  paragraphs: string[];
}

export const parseMarkdown = (
  rawText?: string | null,
): Array<ProfileSection> => {
  if (!rawText) {
    return [];
  }

  const texts = rawText
    .split('\n')
    .map((text) => text.trim())
    .filter(Boolean);

  const sections = texts
    // There is an implicit requirement that all sections begin
    // with a header (a line starting with '#')
    .filter((text) => text.startsWith('#'))
    // We then get the indicies of each heading to use as markers
    // to grab all of the paragraphs underneath a heading, until
    // the next heading
    .map((heading) => texts.indexOf(heading))
    .map((start, startIndex, indexes) => [start, indexes[startIndex + 1]])
    .map(([start, end]) => {
      const [header, ...paragraphs] = texts.slice(start, end);
      return {
        header: header?.slice(1).trim() ?? '', // Drop the '#'
        paragraphs,
      };
    });

  return sections;
};

export const excludeSections = (
  sections: Array<ProfileSection>,
  portfolio?: SaverPortfolio | null,
): Array<ProfileSection> =>
  // We drop the other sections (i.e. "Why we like it") for the index portfolio
  // for compliance reasons, leaving only the "What do they do section"
  portfolio
    ? SaverPortfolio[portfolio] === SaverPortfolio.INDEX
      ? sections.slice(0, 1)
      : sections
    : [];

export const CompanyProfile: React.FC<
  React.PropsWithChildren<CompanyProfileProps>
> = ({ isLoading, portfolio, profile }) => {
  const sections = excludeSections(parseMarkdown(profile?.bodyText), portfolio);

  if (isLoading) {
    return (
      <Box position="absolute" top="50%" left="50%">
        <Spinner />
      </Box>
    );
  }

  return (
    <Columns spaceX="lg">
      {sections.map(({ header, paragraphs }, sectionIndex) => (
        <Columns.Column
          key={`section-${sectionIndex}`}
          width={{ xs: 1, lg: 1 / sections.length }}
        >
          <Stack spaceY="sm">
            <Heading variant={4} color="neutral.000">
              {header}
            </Heading>

            <Stack spaceY="md">
              {paragraphs.map((paragraph, paragraphIndex) => (
                <Text
                  key={`section-${sectionIndex}-paragraph-${paragraphIndex}`}
                  variant={2}
                  color="neutral.015"
                >
                  {paragraph}
                </Text>
              ))}
            </Stack>
          </Stack>
        </Columns.Column>
      ))}
    </Columns>
  );
};
